// @hssna/ui-kit/auth/LoginPage — 登录页面
// 规格: SPEC §6 / §7 (安全合规) / S1 认证流程 / ADR-0012 (Tailwind) / OLD-01 (适老化)
//
// S1 认证流程:
//   1. 用户输入 用户名 + 密码
//   2. 提交调用 authClient.login(username, password)
//   3. 若 mfa_completed=true → onSuccess(loginResponse)
//   4. 若 mfa_completed=false → 切换到 MfaVerifyPage (传入 sessionId + mfaType)
//
// 表单: React Hook Form (校验: 用户名/密码必填, 最小长度)
// 样式: Tailwind + .theme-elderly 适老化变量自动适配

import { useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import type {
  AuthClient,
  LoginResponse,
  MfaVerifyResponse,
} from '@hssna/ts-shared';
import { Button } from '../common/Button.js';
import { Input } from '../common/Input.js';
import { MfaVerifyPage } from './MfaVerifyPage.js';

/** 登录成功后返回的 token 集合 (登录直通 / MFA 验证后) */
export type AuthTokens = LoginResponse | MfaVerifyResponse;

/** MFA 类型 (sms 短信 / totp 时间一次性密码) */
export type MfaType = 'sms' | 'totp';

export interface LoginPageProps {
  /** S1 认证客户端 */
  readonly authClient: AuthClient;
  /** 登录成功回调 (含直通登录与 MFA 验证后两种场景) */
  readonly onSuccess: (tokens: AuthTokens) => void;
  /** 错误回调 (网络错误 / 401 / 429 等) */
  readonly onError?: (error: unknown) => void;
  /** MFA 类型 (登录响应 mfa_completed=false 时使用, 默认 totp) */
  readonly mfaType?: MfaType;
  /** 页面标题 (默认 "HSSNA 登录") */
  readonly title?: string;
}

interface LoginFormValues {
  username: string;
  password: string;
}

export function LoginPage({
  authClient,
  onSuccess,
  onError,
  mfaType = 'totp',
  title = 'HSSNA 登录',
}: LoginPageProps) {
  const [mfaSession, setMfaSession] = useState<{ sessionId: string; type: MfaType } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: { username: '', password: '' },
  });

  // MFA 验证阶段: 渲染 MfaVerifyPage
  if (mfaSession) {
    return (
      <MfaVerifyPage
        authClient={authClient}
        sessionId={mfaSession.sessionId}
        mfaType={mfaSession.type}
        onSuccess={onSuccess}
        onError={onError}
      />
    );
  }

  const onSubmit: SubmitHandler<LoginFormValues> = async (data) => {
    try {
      const response = await authClient.login(data.username, data.password);
      if (response.mfa_completed) {
        // 直通登录 (无 MFA 或 MFA 已完成)
        onSuccess(response);
        return;
      }
      // 需要进行 MFA 验证
      if (!response.session_id) {
        throw new Error('服务端未返回 session_id, 无法启动 MFA 流程');
      }
      setMfaSession({ sessionId: response.session_id, type: mfaType });
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        // 兜底: 控制台输出 (避免静默丢失)
        // eslint-disable-next-line no-console
        console.error('[LoginPage] login failed:', error);
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8 theme-elderly:bg-white">
      <div className="w-full max-w-md">
        <div className="rounded-lg bg-white p-8 shadow-md theme-elderly:border-2 theme-elderly:border-elderly-border">
          <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 theme-elderly:text-elderly-lg">
            {title}
          </h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              label="用户名"
              required
              autoComplete="username"
              placeholder="请输入用户名"
              error={errors.username?.message}
              {...register('username', {
                required: '请输入用户名',
                minLength: { value: 2, message: '用户名至少 2 个字符' },
              })}
            />

            <Input
              label="密码"
              type="password"
              required
              autoComplete="current-password"
              placeholder="请输入密码"
              error={errors.password?.message}
              {...register('password', {
                required: '请输入密码',
                minLength: { value: 6, message: '密码至少 6 个字符' },
              })}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isSubmitting}
              className="w-full theme-elderly:text-elderly"
            >
              {isSubmitting ? '登录中...' : '登录'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
